import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { adviseRoute, getOsrmRoute, suggestAlternatives, suggestRoute, updateRoutePlan } from "@/api/routing";
import type { AlternativeRoute, OsrmRouteSummary, RouteCity, RouteCityWindow, RoutePlan, RoutingQueryInput } from "@/types/routing";
import { createLead, type LeadCreatePayload } from "@/api/crm";
import { createManualBookingLead } from "@/api/booking";
import { compactCityLabel, formatCityHeader } from "@/lib/geography";
import { useToast } from "@/components/ToastProvider";
import { useCapabilities } from "@/hooks/useCapabilities";
import type { CapabilityFlag } from "@/types/capabilities";

const DEFAULT_QUERY: RoutingQueryInput = {
  states: ["CO"],
  start_city: "Denver",
  start_state: "CO",
  end_city: "",
  end_state: "",
  priority_cities: [],
  start_date: null,
  end_date: null,
  max_drive_hours: 6,
  target_show_frequency_days: 2,
  pinned_cities: [],
  use_real_routing: false,
};

const RECOMMENDED_STRATEGY = "recommended";

type LeadGenerationStatus = "idle" | "loading" | "success" | "error";

export function useRouting(initial?: Partial<RoutingQueryInput>) {
  const { push } = useToast();
  const { capabilities, openUpgradeModal } = useCapabilities();
  const canUseRoutingAI = capabilities.canUseRoutingAI;
  const canUseRoutingStrategies = capabilities.canUseRoutingStrategies;
  const canUseRoutingAdvisor = capabilities.canUseRoutingAdvisor;
  const canUseRoutingOsrm = capabilities.canUseRoutingOsrm;
  const canGenerateRoutingLeads = capabilities.canGenerateRoutingLeads;
  const [query, setQuery] = useState<RoutingQueryInput>({ ...DEFAULT_QUERY, ...initial });
  const queryRef = useRef(query);
  const syncQuery = useCallback((next: RoutingQueryInput) => {
    queryRef.current = next;
    setQuery(next);
  }, []);

  const [canonicalPlan, setCanonicalPlan] = useState<RoutePlan | null>(null);
  const [plan, setPlan] = useState<RoutePlan | null>(null);
  const [alternatives, setAlternatives] = useState<AlternativeRoute[]>([]);
  const [loading, setLoading] = useState(false);
  const [advisor, setAdvisor] = useState("");
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [useRealRouting, setUseRealRouting] = useState<boolean>(query.use_real_routing ?? false);
  const [routeDetails, setRouteDetails] = useState<OsrmRouteSummary | null>(null);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [activeStrategy, setActiveStrategy] = useState<string>(RECOMMENDED_STRATEGY);
  const [leadStatus, setLeadStatus] = useState<Record<string, LeadGenerationStatus>>({});

  const resolveAlternatives = useCallback(
    (sourcePlan: RoutePlan | null) => {
      if (alternatives.length) {
        return alternatives;
      }
      return sourcePlan?.alternatives ?? [];
    },
    [alternatives],
  );

  const applyAlternativeStrategy = useCallback(
    (sourcePlan: RoutePlan, strategy: string): RoutePlan => {
      if (strategy === RECOMMENDED_STRATEGY) {
        return sourcePlan;
      }
      const pool = resolveAlternatives(sourcePlan);
      const target = findAlternativeByKind(strategy, pool);
      if (!target) {
        return sourcePlan;
      }
      return dedupePlan(mergePlanWithAlternative(sourcePlan, target));
    },
    [resolveAlternatives],
  );

  const applyServerPlan = useCallback(
    (nextPlan: RoutePlan) => {
      const dedupedPlan = dedupePlan(nextPlan);
      setCanonicalPlan(dedupedPlan);
      if (activeStrategy === RECOMMENDED_STRATEGY) {
        setPlan(dedupedPlan);
        return dedupedPlan;
      }
      const updated = applyAlternativeStrategy(dedupedPlan, activeStrategy);
      if (updated === dedupedPlan) {
        setActiveStrategy(RECOMMENDED_STRATEGY);
        setPlan(dedupedPlan);
        return dedupedPlan;
      }
      setPlan(updated);
      return updated;
    },
    [activeStrategy, applyAlternativeStrategy],
  );

  const handleCapabilityError = useCallback(
    (error: unknown) => {
      const capability = extractCapabilityRequirement(error);
      if (capability) {
        openUpgradeModal(capability);
        return true;
      }
      return false;
    },
    [openUpgradeModal],
  );

  const runSuggest = useCallback(
    async (override?: Partial<RoutingQueryInput>) => {
      if (!canUseRoutingAI) {
        openUpgradeModal("AI routing");
        return null;
      }
      const nextQuery = { ...queryRef.current, ...override };
      syncQuery(nextQuery);
      setLoading(true);
      try {
        const nextPlan = await suggestRoute(nextQuery);
        setLeadStatus({});
        applyServerPlan(nextPlan);
        setAdvisor(nextPlan.llm_advice ?? "");
        setNeedsRefresh(false);
        return nextPlan;
      } catch (error) {
        if (handleCapabilityError(error)) {
          return null;
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [applyServerPlan, canUseRoutingAI, handleCapabilityError, openUpgradeModal, syncQuery],
  );

  const updateQuery = useCallback(
    (partial: Partial<RoutingQueryInput>, autosuggest = false) => {
      const next = { ...queryRef.current, ...partial };
      if ("priority_cities" in partial) {
        const priorities = (partial.priority_cities ?? []).map((label) => label?.trim()).filter(Boolean);
        next.priority_cities = partial.priority_cities ?? [];
      }
      syncQuery(next);
      if (autosuggest) {
        void runSuggest(partial);
      }
    },
    [runSuggest, syncQuery],
  );

  const fetchAlternatives = useCallback(async () => {
    if (!canUseRoutingStrategies) {
      openUpgradeModal("Routing strategies");
      return [];
    }
    try {
      const results = await suggestAlternatives(queryRef.current);
      setAlternatives(results);
      if (canonicalPlan && activeStrategy !== RECOMMENDED_STRATEGY) {
        const target = findAlternativeByKind(activeStrategy, results);
        if (target) {
          setPlan(dedupePlan(mergePlanWithAlternative(canonicalPlan, target)));
        } else {
          setActiveStrategy(RECOMMENDED_STRATEGY);
          setPlan(canonicalPlan);
        }
      }
      return results;
    } catch (error) {
      if (handleCapabilityError(error)) {
        return [];
      }
      throw error;
    }
  }, [activeStrategy, canUseRoutingStrategies, canonicalPlan, handleCapabilityError, openUpgradeModal]);

  const pinCity = useCallback(
    async (cityLabel: string) => {
      if (!plan) return null;
      if (!canUseRoutingAI) {
        openUpgradeModal("AI routing");
        return plan;
      }
      setLoading(true);
      try {
        const updated = await updateRoutePlan(plan, cityLabel);
        setLeadStatus({});
        applyServerPlan(updated);
        setAdvisor(updated.llm_advice ?? "");
        setNeedsRefresh(false);
        return updated;
      } catch (error) {
        if (handleCapabilityError(error)) {
          return plan;
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [applyServerPlan, canUseRoutingAI, handleCapabilityError, openUpgradeModal, plan],
  );

  const reorderPlan = useCallback(
    (names: string[]) => {
      setPlan((previous) => (previous ? { ...previous, ordered_city_names: names } : previous));
      if (activeStrategy === RECOMMENDED_STRATEGY) {
        setCanonicalPlan((previous) => (previous ? { ...previous, ordered_city_names: names } : previous));
      }
      setNeedsRefresh(true);
    },
    [activeStrategy],
  );

  const requestAdvice = useCallback(async () => {
    if (!plan) return "";
    if (!canUseRoutingAdvisor) {
      openUpgradeModal("Routing advisor");
      return "";
    }
    setAdvisorLoading(true);
    try {
      const result = await adviseRoute(plan);
      setAdvisor(result.advice);
      return result.advice;
    } catch (error) {
      if (handleCapabilityError(error)) {
        return "";
      }
      throw error;
    } finally {
      setAdvisorLoading(false);
    }
  }, [canUseRoutingAdvisor, handleCapabilityError, openUpgradeModal, plan]);

  const strategyOptions = useMemo(
    () => resolveAlternatives(canonicalPlan ?? plan),
    [canonicalPlan, plan, resolveAlternatives],
  );

  const state = useMemo(
    () => ({
      query,
      canonicalPlan,
      plan,
      loading,
      advisor,
      advisorLoading,
      alternatives,
      strategies: strategyOptions,
      needsRefresh,
      useRealRouting,
      routeDetails,
      routeError,
      activeStrategy,
      leadStatus,
    }),
    [
      query,
      canonicalPlan,
      plan,
      loading,
      advisor,
      advisorLoading,
      alternatives,
      strategyOptions,
      needsRefresh,
      useRealRouting,
      routeDetails,
      routeError,
      activeStrategy,
      leadStatus,
    ],
  );

  const fetchRouteDetails = useCallback(
    async (currentPlan: RoutePlan) => {
      if (!canUseRoutingOsrm) {
        setRouteDetails(null);
        setRouteError(null);
        return;
      }
      if (!currentPlan?.ordered_city_names?.length) {
        setRouteDetails(null);
        setRouteError(null);
        return;
      }
      const cityLookup = new Map<string, RoutePlan["cities"][number]>();
      currentPlan.cities.forEach((city) => {
        const key = compactCityLabel(city.city, city.state);
        if (!key) return;
        cityLookup.set(key, city);
      });
      const points = currentPlan.ordered_city_names
        .map((label) => {
          const { city, state } = splitCityLabel(label);
          return cityLookup.get(compactCityLabel(city, state));
        })
        .filter((city): city is NonNullable<typeof city> => Boolean(city))
        .map((city) => ({ lat: city.latitude, lon: city.longitude }));
      if (points.length < 2) {
        setRouteDetails(null);
        setRouteError(null);
        return;
      }
      try {
        const response = await getOsrmRoute(points);
        setRouteDetails(response);
        setRouteError(null);
      } catch (error) {
        setRouteDetails(null);
        const message = "OSRM unavailable; using estimated routing.";
        setRouteError(message);
        push({ title: "Routing fallback", content: message, type: "warning" });
      }
    },
    [canUseRoutingOsrm, push],
  );

  useEffect(() => {
    if (useRealRouting && plan && canUseRoutingOsrm) {
      void fetchRouteDetails(plan);
    } else {
      setRouteDetails(null);
      setRouteError(null);
    }
  }, [canUseRoutingOsrm, useRealRouting, plan, fetchRouteDetails]);

  const toggleRealRouting = useCallback(
    (value: boolean) => {
      if (value && !canUseRoutingOsrm) {
        openUpgradeModal("Drive-time optimization");
        return;
      }
      setUseRealRouting(value);
      updateQuery({ use_real_routing: value }, false);
    },
    [canUseRoutingOsrm, openUpgradeModal, updateQuery],
  );

  const selectStrategy = useCallback(
    (kind: string) => {
      if (!canonicalPlan) return;
       if (!canUseRoutingStrategies) {
        openUpgradeModal("Routing strategies");
        return;
      }
      if (kind === RECOMMENDED_STRATEGY) {
        setPlan(canonicalPlan);
        setActiveStrategy(RECOMMENDED_STRATEGY);
        return;
      }
      const pool = resolveAlternatives(canonicalPlan);
      const target = findAlternativeByKind(kind, pool);
      if (!target) {
        push({
          title: "Strategy unavailable",
          content: "Recalculate routing to refresh alternative plans.",
          type: "warning",
        });
        setActiveStrategy(RECOMMENDED_STRATEGY);
        setPlan(canonicalPlan);
        return;
      }
      setPlan(dedupePlan(mergePlanWithAlternative(canonicalPlan, target)));
      setActiveStrategy(kind);
    },
    [canUseRoutingStrategies, canonicalPlan, openUpgradeModal, push, resolveAlternatives],
  );

  const generateLeadsForCity = useCallback(
    async (cityLabel: string, venueOptions?: number[]) => {
      if (!canGenerateRoutingLeads) {
        openUpgradeModal("Routing lead sync");
        return;
      }
      if (!plan) {
        push({ title: "No routing plan", content: "Calculate a route before creating leads.", type: "warning" });
        return;
      }
      const normalizedLabel = cityLabel.trim();
      const cityRecord = plan.cities.find((city) => compactCityLabel(city.city, city.state) === normalizedLabel);
      if (!cityRecord) {
        push({ title: "City not found", content: "Refresh routing to sync venue details.", type: "warning" });
        return;
      }
      const windowLookup = new Map(
        plan.city_windows.map((window) => [compactCityLabel(window.city, window.state), window]),
      );
      const window = windowLookup.get(normalizedLabel);
      const defaultVenueIds = Array.from(new Set(cityRecord.venue_ids ?? []));
      const header = formatCityHeader(cityRecord.city, cityRecord.state);
      const requestedVenueIds = venueOptions && venueOptions.length ? venueOptions : defaultVenueIds;
      const venueNames = cityRecord.venue_names ?? [];
      type RoutingVenueCandidate = {
        id: number;
        name?: string;
        email?: string | null;
        phone?: string | null;
        tags?: string[];
        company_name?: string | null;
        last_booking_score?: number | null;
      };
      let candidateVenues: RoutingVenueCandidate[] =
        cityRecord.venues && cityRecord.venues.length
          ? cityRecord.venues
              .filter((venue) => (requestedVenueIds.length ? requestedVenueIds.includes(venue.id) : true))
              .map((venue) => ({
                id: venue.id,
                name: venue.name,
                email: venue.email,
                phone: venue.phone,
                tags: venue.tags ?? [],
                company_name: venue.company_name ?? null,
                last_booking_score: venue.last_booking_score ?? null,
              }))
          : [];
      if (!candidateVenues.length) {
        const fallbackIds = requestedVenueIds.length ? requestedVenueIds : defaultVenueIds;
        if (!fallbackIds.length) {
          push({
            title: "No CRM venues",
            content: `No synced contacts for ${cityRecord.city}.`,
            type: "warning",
          });
          return;
        }
        candidateVenues = fallbackIds.map((contactId, index) => ({
          id: contactId,
          name:
            venueNames[index] ??
            (venueNames.length ? venueNames[index % venueNames.length] : undefined) ??
            header,
          tags: [],
          email: undefined,
          phone: undefined,
          company_name: undefined,
          last_booking_score: undefined,
        }));
      }
      setLeadStatus((prev) => ({ ...prev, [normalizedLabel]: "loading" }));
      const [windowStart, windowEnd] = extractWindowDates(window);
      try {
        const results = await Promise.allSettled(
          candidateVenues.map(async (venue) => {
            const payload: LeadCreatePayload = {
              contact_id: venue.id,
              title: `${venue.name ?? header} • ${header}`,
              source: "Routing Agent",
              status: "open",
              pipeline_stage: "Opportunity",
              region: compactCityLabel(cityRecord.city, cityRecord.state),
              event_window: windowStart && windowEnd ? { start: windowStart, end: windowEnd } : null,
              description: `Generated from the routing cluster for ${header}.`,
              metadata: {
                routing_city_label: normalizedLabel,
                routing_window_start: windowStart,
                routing_window_end: windowEnd,
              },
            };
            const leadRecord = await createLead(payload);
            await createManualBookingLead({
              contact_id: venue.id,
              crm_lead_id: leadRecord?.id,
              company_name: venue.company_name ?? venue.name,
              contact_name: venue.name,
              contact_email: venue.email ?? undefined,
              phone: venue.phone ?? undefined,
              city: cityRecord.city,
              state: cityRecord.state,
              pipeline_stage: "Prospecting",
              tags: venue.tags ?? undefined,
              window_start: windowStart ?? undefined,
              window_end: windowEnd ?? undefined,
              source: "Routing Agent",
              metadata: {
                routing_city_label: normalizedLabel,
                routing_selection: venueOptions?.length ? "partial" : "all",
                routing_window_start: windowStart,
                routing_window_end: windowEnd,
              },
              score: typeof venue.last_booking_score === "number" ? Number(venue.last_booking_score) : undefined,
            });
            return leadRecord;
          }),
        );
        const successes = results.filter((result) => result.status === "fulfilled").length;
        const failures = results.length - successes;
        if (successes) {
          push({
            title: "Leads created",
            content: `${successes} lead${successes === 1 ? "" : "s"} synced to Booking.`,
            type: "success",
          });
        }
        if (failures) {
          push({
            title: "Some leads failed",
            content: `${failures} record${failures === 1 ? "" : "s"} could not sync. Try again after refreshing.`,
            type: "warning",
          });
        }
        setLeadStatus((prev) => ({
          ...prev,
          [normalizedLabel]: failures ? "error" : "success",
        }));
      } catch (error) {
        if (handleCapabilityError(error)) {
          setLeadStatus((prev) => ({ ...prev, [normalizedLabel]: "error" }));
          return;
        }
        setLeadStatus((prev) => ({ ...prev, [normalizedLabel]: "error" }));
        push({
          title: "Lead sync failed",
          content: error instanceof Error ? error.message : "Unable to sync booking leads.",
          type: "error",
        });
      }
    },
    [canGenerateRoutingLeads, handleCapabilityError, openUpgradeModal, plan, push],
  );

  return {
    ...state,
    suggestRoute: runSuggest,
    updateQuery,
    fetchAlternatives,
    pinCity,
    reorderPlan,
    requestAdvice,
    setUseRealRouting: toggleRealRouting,
    setStrategy: selectStrategy,
    generateLeads: generateLeadsForCity,
  };
}

function findAlternativeByKind(kind: string, pool: AlternativeRoute[]): AlternativeRoute | null {
  if (!kind) return null;
  return pool.find((alternative) => alternative.kind === kind) ?? null;
}

function dedupePlan(plan: RoutePlan): RoutePlan {
  const basePlan: RoutePlan = {
    ...plan,
    cities: [...(plan.cities || [])],
    city_windows: [...(plan.city_windows || [])],
    ordered_city_names: [...(plan.ordered_city_names || [])],
    pinned_cities: [...(plan.pinned_cities || [])],
    drive_hours_matrix: { ...(plan.drive_hours_matrix || {}) },
  };

  const cityBuckets = aggregateCities(basePlan.cities);
  const windowBuckets = aggregateWindows(basePlan.city_windows);
  const { order, labels, pinned } = deriveLabelOrder(basePlan);
  const normalizedMatrix = normalizeDriveMatrix(basePlan.drive_hours_matrix);

  const dedupCities = order.map((key, index) => {
    const city = cityBuckets.get(key);
    if (city) return city;
    const { city: parsedCity, state: parsedState } = splitCityLabel(labels[index]);
    return {
      city: parsedCity,
      state: parsedState,
      latitude: 0,
      longitude: 0,
      venue_ids: null,
      market_score: null,
      venue_names: [],
    };
  });

  const dedupWindows = order.map((key, index) => {
    const window = windowBuckets.get(key);
    if (window) return window;
    const { city, state } = splitCityLabel(labels[index]);
    return {
      city,
      state,
      notes: "Dates pending; confirm target window.",
    };
  });

  const dedupMatrix = rebuildDriveMatrix(order, labels, normalizedMatrix);

  return {
    ...basePlan,
    cities: dedupCities,
    ordered_city_names: labels,
    pinned_cities: pinned,
    city_windows: dedupWindows,
    drive_hours_matrix: dedupMatrix,
  };
}

function aggregateCities(cities: RouteCity[]): Map<string, RouteCity> {
  const buckets = new Map<string, RouteCity>();
  cities.forEach((city) => {
    const key = normalizeCityKey(city.city, city.state);
    if (!key) return;
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, cloneCity(city));
      return;
    }
    mergeCity(existing, city);
  });
  return buckets;
}

function aggregateWindows(windows: RouteCityWindow[]): Map<string, RouteCityWindow> {
  const buckets = new Map<string, RouteCityWindow>();
  windows.forEach((window) => {
    const key = normalizeCityKey(window.city, window.state);
    if (!key) return;
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, { ...window });
      return;
    }
    buckets.set(key, mergeWindow(existing, window));
  });
  return buckets;
}

function deriveLabelOrder(plan: RoutePlan) {
  const order: string[] = [];
  const labels: string[] = [];
  const pinned: string[] = [];
  const seen = new Map<string, number>();

  plan.ordered_city_names.forEach((label, index) => {
    const trimmed = label?.trim();
    if (!trimmed) return;
    const { city, state } = splitCityLabel(trimmed);
    const key = normalizeCityKey(city, state);
    if (!key) return;
    if (!seen.has(key)) {
      seen.set(key, order.length);
      order.push(key);
      const formattedLabel = formatCityLabel(city, state);
      labels.push(formattedLabel);
      const pinnedLabel = plan.pinned_cities?.[index]?.trim() ? formattedLabel : "";
      pinned.push(pinnedLabel);
    } else if (plan.pinned_cities?.[index]?.trim()) {
      const targetIndex = seen.get(key)!;
      pinned[targetIndex] = labels[targetIndex];
    }
  });

  if (!order.length && plan.cities.length) {
    plan.cities.forEach((city) => {
      const key = normalizeCityKey(city.city, city.state);
      if (!key || seen.has(key)) return;
      seen.set(key, order.length);
      order.push(key);
      labels.push(formatCityLabel(city.city, city.state));
      pinned.push("");
    });
  }

  return { order, labels, pinned };
}

function normalizeDriveMatrix(matrix: RoutePlan["drive_hours_matrix"]) {
  const normalized: Record<string, Record<string, number>> = {};
  Object.entries(matrix || {}).forEach(([originLabel, destinations]) => {
    const originKey = normalizeLabel(originLabel);
    if (!originKey) return;
    normalized[originKey] = normalized[originKey] || {};
    Object.entries(destinations || {}).forEach(([destLabel, hours]) => {
      const destKey = normalizeLabel(destLabel);
      if (!destKey || typeof hours !== "number") return;
      if (!(destKey in normalized[originKey])) {
        normalized[originKey][destKey] = hours;
      }
    });
  });
  return normalized;
}

function rebuildDriveMatrix(
  order: string[],
  labels: string[],
  normalizedMatrix: Record<string, Record<string, number>>,
) {
  const rebuilt: Record<string, Record<string, number>> = {};
  order.forEach((originKey, originIndex) => {
    const originLabel = labels[originIndex];
    const destinations = normalizedMatrix[originKey] ?? {};
    rebuilt[originLabel] = {};
    order.forEach((destKey, destIndex) => {
      const destLabel = labels[destIndex];
      const value = destinations[destKey];
      if (typeof value === "number") {
        rebuilt[originLabel][destLabel] = value;
      }
    });
  });
  return rebuilt;
}

function cloneCity(city: RouteCity): RouteCity {
  return {
    ...city,
    venue_ids: city.venue_ids ? [...city.venue_ids] : null,
    venue_names: city.venue_names ? [...city.venue_names] : [],
  };
}

function mergeCity(target: RouteCity, incoming: RouteCity) {
  const venueIds = new Set<number>();
  (target.venue_ids || []).forEach((id) => venueIds.add(id));
  (incoming.venue_ids || []).forEach((id) => venueIds.add(id));
  target.venue_ids = venueIds.size ? Array.from(venueIds) : null;

  const venueNames = new Set<string>();
  (target.venue_names || []).forEach((name) => venueNames.add(name));
  (incoming.venue_names || []).forEach((name) => venueNames.add(name));
  target.venue_names = Array.from(venueNames);

  if (target.market_score == null && incoming.market_score != null) {
    target.market_score = incoming.market_score;
  }
  if (!target.latitude && incoming.latitude) {
    target.latitude = incoming.latitude;
  }
  if (!target.longitude && incoming.longitude) {
    target.longitude = incoming.longitude;
  }
}

function mergeWindow(existing: RouteCityWindow, incoming: RouteCityWindow): RouteCityWindow {
  return {
    city: existing.city || incoming.city,
    state: existing.state || incoming.state,
    arrival_date: pickEarliest(existing.arrival_date, incoming.arrival_date),
    earliest_date: pickEarliest(existing.earliest_date, incoming.earliest_date),
    latest_date: pickLatest(existing.latest_date, incoming.latest_date),
    notes: existing.notes || incoming.notes,
    booking_range_start: pickEarliest(existing.booking_range_start, incoming.booking_range_start),
    booking_range_end: pickLatest(existing.booking_range_end, incoming.booking_range_end),
  };
}

function pickEarliest(a?: string | null, b?: string | null): string | null {
  if (!a) return b ?? null;
  if (!b) return a ?? null;
  return new Date(a) <= new Date(b) ? a : b;
}

function pickLatest(a?: string | null, b?: string | null): string | null {
  if (!a) return b ?? null;
  if (!b) return a ?? null;
  return new Date(a) >= new Date(b) ? a : b;
}

function normalizeCityKey(city?: string | null, state?: string | null): string {
  const key = compactCityLabel(city ?? "", state ?? "");
  return key ? key.trim().toLowerCase() : "";
}

function splitCityLabel(label: string): { city: string; state: string } {
  const [cityPart, ...stateParts] = label.split(",");
  return {
    city: (cityPart ?? "").trim(),
    state: stateParts.join(",").trim(),
  };
}

function formatCityLabel(city: string, state: string): string {
  return compactCityLabel(city, state);
}

function normalizeLabel(label: string): string {
  const { city, state } = splitCityLabel(label);
  return normalizeCityKey(city, state);
}

function mergePlanWithAlternative(plan: RoutePlan, alternative: AlternativeRoute): RoutePlan {
  const windowsByLabel = new Map(
    plan.city_windows.map((window) => [compactCityLabel(window.city, window.state), window]),
  );
  const pinnedLabels = new Set(plan.pinned_cities.filter((label) => label && label.trim()));
  const orderedCityNames = alternative.ordered_city_names.length ? alternative.ordered_city_names : plan.ordered_city_names;
  const nextWindows = orderedCityNames.map((label) => {
    const match = windowsByLabel.get(label);
    if (match) return match;
    const [city, state = ""] = label.split(",").map((token) => token.trim());
    return {
      city,
      state,
    } as RoutePlan["city_windows"][number];
  });
  const nextPinned = orderedCityNames.map((label) => (pinnedLabels.has(label) ? label : ""));
  return {
    ...plan,
    ordered_city_names: orderedCityNames,
    drive_hours_matrix: alternative.drive_hours_matrix,
    city_windows: nextWindows,
    pinned_cities: nextPinned,
  };
}

function extractWindowDates(
  window?: RoutePlan["city_windows"][number],
): [string | null, string | null] {
  if (!window) {
    return [null, null];
  }
  const start =
    normalizeDate(window.booking_range_start) ||
    normalizeDate(window.earliest_date) ||
    normalizeDate(window.arrival_date);
  const end =
    normalizeDate(window.booking_range_end) ||
    normalizeDate(window.latest_date) ||
    normalizeDate(window.arrival_date);
  return [start, end];
}

function normalizeDate(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 10);
}

type CapabilityErrorPayload = {
  error?: string;
  capability?: CapabilityFlag;
};

type ApiCapabilityError = Error & {
  status?: number;
  payload?: CapabilityErrorPayload | null;
};

function extractCapabilityRequirement(error: unknown): CapabilityFlag | null {
  if (!error || typeof error !== "object") {
    return null;
  }
  const payload = (error as ApiCapabilityError).payload;
  const status = (error as ApiCapabilityError).status;
  if (status === 403 && payload?.error === "requires_pro") {
    return (payload.capability as CapabilityFlag) ?? null;
  }
  return null;
}
